import { useState } from "react";
import { Card, DataTable, Badge, Button, Text, Link, Tooltip, EmptyState, BlockStack, InlineStack } from "@shopify/polaris";
import { getRequest } from "../../../shared/components/functions";

const dateOf = (value) => (value ? String(value).substring(0, 10) : "");

const STATUS_BADGE = {
  clear: <Badge tone="success">Clear to build</Badge>,
  partial: <Badge tone="attention">Partially buildable</Badge>,
  blocked: <Badge tone="critical">Blocked</Badge>,
  unresolved: <Badge>No BOM resolved</Badge>,
};

const constraintChips = (constraints) => {
  if (!constraints || constraints.length === 0) return "";
  return (
    <InlineStack gap="100" wrap>
      {constraints.slice(0, 4).map((constraint) => (
        <Tooltip
          key={constraint.productId}
          content={`Missing ${constraint.missingQty} (need ${constraint.requiredQty}, have ${constraint.availableQty})${
            constraint.earliestInboundDate ? ` — inbound ${constraint.earliestInboundDate}` : " — nothing inbound"
          }`}>
          <Link removeUnderline url={`/products/${constraint.productId}`}>
            <Badge tone="critical" size="small">
              {String(constraint.productName || constraint.productId).substring(0, 24)}
            </Badge>
          </Link>
        </Tooltip>
      ))}
      {constraints.length > 4 && <Badge size="small">{`+${constraints.length - 4} more`}</Badge>}
    </InlineStack>
  );
};

export function ClearToBuild({ clearToBuild: initialItems, generatedAt }) {
  const [items, setItems] = useState(initialItems || []);
  const [refreshedAt, setRefreshedAt] = useState(generatedAt);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const data = await getRequest("/api/mrp/clear-to-build");
    if (data && data.clearToBuild) {
      setItems(data.clearToBuild);
      setRefreshedAt(data.generatedAt);
    }
    setLoading(false);
  };

  if (!items.length) {
    return (
      <Card>
        <EmptyState heading="Nothing planned to build" image="">
          <p>No open work orders or machine orders were found in the last run.</p>
          <Button onClick={refresh} loading={loading}>
            Refresh live
          </Button>
        </EmptyState>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text tone="subdued" as="span">{`Snapshot: ${refreshedAt ? String(refreshedAt).replace("T", " ").substring(0, 16) : "last run"}`}</Text>
          <Button onClick={refresh} loading={loading}>
            Refresh live
          </Button>
        </InlineStack>

        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          columnContentTypes={["text", "text", "text", "numeric", "numeric", "numeric", "text", "text"]}
          headings={[<b>Order</b>, <b>Build</b>, <b>Due</b>, <b>Qty</b>, <b>Buildable now</b>, <b>With inbound</b>, <b>Status</b>, <b>Constraint parts</b>]}
          rows={items.map((item) => [
            item.workOrderId ? (
              <Link removeUnderline url="/workorders">
                {item.ref}
              </Link>
            ) : (
              item.ref
            ),
            <Link removeUnderline url={`/products/${item.productId}`}>
              {String(item.productName || item.productId).substring(0, 36)}
            </Link>,
            dateOf(item.needDate),
            item.qty,
            <Text as="span" fontWeight="bold" tone={item.buildableNowQty >= item.qty ? "success" : item.buildableNowQty > 0 ? undefined : "critical"}>
              {item.buildableNowQty}
            </Text>,
            item.buildableWithInboundQty,
            STATUS_BADGE[item.status] || <Badge>{item.status}</Badge>,
            constraintChips(item.constraints),
          ])}
        />
      </BlockStack>
    </Card>
  );
}
